import { AfterViewInit, ChangeDetectorRef, Component, ElementRef, EventEmitter, HostListener, Input, OnChanges, Output, SimpleChanges, ViewChild } from '@angular/core';
import { DomSanitizer, SafeHtml } from '@angular/platform-browser';
import { ToastrService } from 'ngx-toastr';
import { Observable } from 'rxjs';
import * as go from 'gojs';
import { InstantiatedEquipmentItem, SchematicDataOut, SchematicEdgeDataIn, SchematicNodeDataIn, SchematicSaveDataOut, SchematicSaveRequest, SchematicValidationIssue, SchematicValidationNodeDataIn, SchematicValidationSeverity } from '../../models/schematic.model';
import { SchematicService } from '../../services/schematic.service';
import { CimSubstationService } from '../../services/cim-substation.service';
import { EquipmentListService } from '../../services/equipment-list.service';
import { SelectSubstationDataIn } from '../../models/substation-template-instance.model';
import { SchematicStatus } from '../../enums/schematic-status';
import { DiagramStatus } from '../../enums/diagram-status';
import { PermissionsService } from '../../services/permissions.service';

import { SYMBOLS, themeColor } from './gojs-schematic.symbols';
import { busbarNode, acLineNode, symNode, CIM_FRAME, CIM_TAG } from './gojs-schematic.templates';
import {
  cleanupAllPorts,
  withoutUndo,
  busbarFraction,
  isPortLinked,
  BusbarAwareRelinkingTool,
  BusbarRightClickLinkingTool,
  PortShowingLinkingTool,
  SyncLabelDraggingTool,
} from './gojs-schematic.tools';

interface TopologyEntry {
  label: string;
  sublabel: string;
  navigate: () => void;
  disconnect: () => void;
  navTitle: string;
}

@Component({
  selector: 'lib-gojs-schematic',
  standalone: false,
  templateUrl: './gojs-schematic.component.html',
  styleUrls: ['./gojs-schematic.component.scss'],
})
export class GojsSchematicComponent implements AfterViewInit, OnChanges {
  @ViewChild('canvasWrapper') canvasWrapper!: ElementRef<HTMLDivElement>;

  diagram!: go.Diagram;
  zoomPercent = 100;

  selectedNode: go.Node | null = null;
  annotationTag = '';
  scaleValue = 1.0;
  nodeLength = 280;
  selectedColor = '';
  selectedState = 'closed';

  selectedLink: go.Link | null = null;
  linkColor = '#ffffff';

  isLightTheme = false;
  private drawLight = false;

  private _busbarRightClickTool!: BusbarRightClickLinkingTool;

  canUndo = false;
  canRedo = false;

  readonly DiagramStatus = DiagramStatus;
  diagramStatus: DiagramStatus = DiagramStatus.Empty;
  private _autoSaveTimer: ReturnType<typeof setTimeout> | null = null;
  private _diagramReady = false;

  schematicStatus: number | null = null;
  isPublished: boolean = false;
  isDraft: boolean = false;
  isPrePublish: boolean = false;
  isRejected: boolean = false;
  schematicLoaded: boolean = false;
  editMode: boolean = false;
  editModeDirty: boolean = false;

  private lastKnownUpdateTime: string | null = null;

  get canEdit(): boolean {
    return this.permissionsService.isSchematicDiagramCreateEditAllowed;
  }

  get isLocked(): boolean {
    return !this.canEdit || ((this.isPublished || this.isPrePublish) && !this.editMode);
  }

  get hasUnpublishedEditChanges(): boolean {
    return this.isPublished && this.editMode && this.editModeDirty;
  }

  // Found by validateAgainstCim
  cimSubstationIssue: string | null = null;
  cimNodeIssues: string[] = [];
  cimNodeInfos: string[] = [];

  get hasCimIssues(): boolean {
    return !!this.cimSubstationIssue || this.cimNodeIssues.length > 0;
  }

  // Set from the Publish click until the publish request finishes or fails, so a second click can't start another one.
  publishing = false;

  get canExport(): boolean {
    return this.isPublished && !this.hasUnpublishedEditChanges;
  }

  enterEditMode(): void {
    this.editMode = true;
    this.editModeDirty = false;
    this.applyLockState();
  }

  exportMenuOpen = false;
  @ViewChild('exportWrapper') exportWrapper?: ElementRef<HTMLDivElement>;

  toggleExportMenu(): void {
    this.exportMenuOpen = !this.exportMenuOpen;
  }

  toggleTheme(): void {
    this.isLightTheme = !this.isLightTheme;
    this.applyCanvasTheme(this.isLightTheme);
    this.buildLegendEntries();
  }

  private applyCanvasTheme(light: boolean): void {
    this.drawLight = light;
    withoutUndo(this.diagram, () => {
      const selected = this.clearSelectionForSnapshot();
      this.diagram.updateAllTargetBindings();
      this.resyncPortVisuals();
      this.restoreSelection(selected);
    });
  }

  @HostListener('document:pointerdown', ['$event'])
  closeExportMenuOnOutsideClick(event: PointerEvent): void {
    if (this.exportMenuOpen && !this.exportWrapper?.nativeElement.contains(event.target as Node))
      this.exportMenuOpen = false;
  }

  exportAsJpg(): void {
    this.exportMenuOpen = false;
    this.renderDiagramSnapshot('image/jpeg', (dataUrl) => {
      const link = document.createElement('a');
      link.href = dataUrl;
      link.download = `${this.exportFileBaseName()}.jpg`;
      link.click();
    });
  }

  exportAsPdf(): void {
    this.exportMenuOpen = false;
    this.renderDiagramSnapshot('image/png', (dataUrl) => this.printDiagramImage(dataUrl));
  }

  private printDiagramImage(dataUrl: string): void {
    const style = document.createElement('style');
    style.textContent = `
      @media print {
        body { margin: 0; }
        body > :not(#schematic-print-image) { display: none !important; }
        #schematic-print-image {
          display: block !important;
          position: fixed;
          top: 50%;
          left: 50%;
          transform: translate(-50%, -50%);
          max-width: 100%;
          max-height: 100%;
        }
      }
    `;
    const img = document.createElement('img');
    img.id = 'schematic-print-image';
    img.style.display = 'none';

    const originalTitle = document.title;
    const cleanup = () => {
      img.remove();
      style.remove();
      document.title = originalTitle;
      window.removeEventListener('afterprint', cleanup);
    };
    window.addEventListener('afterprint', cleanup);

    document.body.appendChild(style);
    document.body.appendChild(img);
    img.onload = () => {
      document.title = this.exportFileBaseName();
      window.print();
    };
    img.src = dataUrl;
  }

  private revealHiddenLabels(): go.Node[] {
    const revealed: go.Node[] = [];
    withoutUndo(this.diagram, () => {
      this.diagram.nodes.each((node: go.Node) => {
        if (node.data?.hiddenUntilHover && node.opacity === 0) {
          node.opacity = 1;
          revealed.push(node);
        }
      });
    });
    return revealed;
  }

  private restoreHiddenLabels(nodes: go.Node[]): void {
    withoutUndo(this.diagram, () => {
      nodes.forEach((node) => (node.opacity = 0));
    });
  }

  // The CIM WARNING/INFO markers are only for the editor: exports and the published image (which Adora and the data lake receive) must never show them.
  private hideCimMarkers(): go.GraphObject[] {
    const hidden: go.GraphObject[] = [];
    withoutUndo(this.diagram, () => {
      this.diagram.nodes.each((node: go.Node) => {
        [node.findObject(CIM_FRAME), node.findObject(CIM_TAG)].forEach((marker) => {
          if (marker?.visible) {
            marker.visible = false;
            hidden.push(marker);
          }
        });
      });
    });
    this.diagram.ensureBounds();
    return hidden;
  }

  private restoreCimMarkers(markers: go.GraphObject[]): void {
    withoutUndo(this.diagram, () => {
      markers.forEach((marker) => (marker.visible = true));
    });
  }

  private clearSelectionForSnapshot(): go.Part[] {
    const selected: go.Part[] = [];
    this.diagram.selection.each((part: go.Part) => selected.push(part));
    if (selected.length > 0) this.diagram.clearSelection(true);
    return selected;
  }

  private restoreSelection(parts: go.Part[]): void {
    if (parts.length > 0) this.diagram.selectCollection(parts);
  }

  private renderDiagramSnapshot(type: 'image/png' | 'image/jpeg', callback: (dataUrl: string) => void, forceDark: boolean = false): void {
    const drawDarkForSnapshot = forceDark && this.drawLight;
    if (drawDarkForSnapshot) this.applyCanvasTheme(false);
    const hiddenCimMarkers = this.hideCimMarkers();
    const bounds = this.diagram.documentBounds;
    const revealedLabels = this.revealHiddenLabels();
    const previousSelection = this.clearSelectionForSnapshot();
    const maxSide = 8000;
    const scale = Math.min(6, maxSide / bounds.width, maxSide / bounds.height);

    this.diagram.makeImageData({
      background: this.drawLight ? '#ffffff' : '#000000',
      type,
      position: bounds.position,
      scale,
      maxSize: new go.Size(maxSide, maxSide),
      callback: (dataUrl: string) => {
        this.restoreHiddenLabels(revealedLabels);
        this.restoreCimMarkers(hiddenCimMarkers);
        this.restoreSelection(previousSelection);
        if (drawDarkForSnapshot) this.applyCanvasTheme(true);
        callback(dataUrl);
      },
    });
  }

  private exportFileBaseName(): string {
    const name = (this.substationName ?? '').trim().replace(/[\\/:*?"<>|]+/g, '-');
    return `${name || 'schematic'}-schema`;
  }

  private nodeTopologyLinks: Array<{ linkData: any; label: string; sublabel: string }> = [];
  private linkTopologyNodes: Array<{ nodeKey: any; label: string; sublabel: string }> = [];

  legendOpen = false;
  legendEntries: Array<{ name: string; iconSvg: SafeHtml }> = [];

  readonly PALETTE_COLORS = [
    '#ef4444', '#f97316', '#eab308', '#22c55e', '#10b981',
    '#06b6d4', '#3b82f6', '#8b5cf6', '#ec4899', '#f8fafc',
  ];

  @Input() substationId: number | null = null;
  @Input() substationName: string | null = null;
  @Input() viewOnly: boolean = false;
  @Input() showSchemaAssetPicker: boolean = false;
  @Output() closeSchematic = new EventEmitter<void>();

  sections: Record<string, boolean> = {
    connector:    true,
    switchgear:   true,
    grounding:    true,
    protection:   true,
    compensation: true,
  };

  searchQuery = '';

  private readonly symbolLabels: Record<string, string> = {
    BB:  'BUSBAR',
    JN:  'JUNCTION',
    LN:  'LINE',
    TR:  'TRANSFORMER',
    JP:  'JUMPER',
    FS:  'FUSE',
    DS:  'DISCONNECTOR',
    GD:  'GROUND DISCONNECTOR',
    CB:  'CIRCUIT BREAKER',
    LBS: 'LOAD BREAK SWITCH',
    GND: 'GROUND',
    CUT: 'CUT',
    REC: 'RECLOSER',
    SEC: 'SECTIONALISER',
    PC:  'PETERSEN COIL',
    SC:  'SHUNT COMPENSATOR',
    FC:  'FREQUENCY CONVERTER',
    SVC: 'STATIC VAR COMPENSATOR',
  };

  private readonly sectionItems: Record<string, string[]> = {
    connector:    ['BUSBAR', 'JUNCTION', 'LINE', 'TRANSFORMER'],
    switchgear:   ['JUMPER', 'FUSE', 'DISCONNECTOR', 'GROUND DISCONNECTOR', 'CIRCUIT BREAKER', 'LOAD BREAK SWITCH'],
    grounding:    ['GROUND', 'CUT'],
    protection:   ['RECLOSER', 'SECTIONALISER'],
    compensation: ['PETERSEN COIL', 'SHUNT COMPENSATOR', 'FREQUENCY CONVERTER', 'STATIC VAR COMPENSATOR'],
  };

  itemVisible(type: string): boolean {
    if (!this.searchQuery) return true;
    return type.toLowerCase().includes(this.searchQuery.toLowerCase());
  }

  sectionVisible(key: string): boolean {
    if (!this.searchQuery) return true;
    return (this.sectionItems[key] ?? []).some(item => this.itemVisible(item));
  }

  paletteTab: 'equipment' | 'instantiated' = 'equipment';
  instantiatedEquipment: InstantiatedEquipmentItem[] = [];
  private cimTerminalsByEquipmentId = new Map<number, InstantiatedEquipmentItem['terminals']>();
  private destinationCountByEquipmentId = new Map<number, number>();
  private hubConnectivityNodeIds = new Set<number>();
  instantiatedSearchQuery = '';

  readonly instantiatedSectionKeys = [...Object.keys(this.sectionItems), 'other'];
  readonly sectionTitles: Record<string, string> = {
    connector:    'CONNECTOR',
    switchgear:   'SWITCHGEAR',
    grounding:    'GROUNDING & SECTIONING',
    protection:   'PROTECTION DEVICES',
    compensation: 'COMPENSATION EQUIPMENT',
    other:        'OTHER',
  };
  instantiatedSections: Record<string, boolean> = Object.fromEntries(
    this.instantiatedSectionKeys.map(key => [key, true])
  );

  equipmentTypeLabel(iconCode: string): string {
    return this.symbolLabels[iconCode] ?? iconCode;
  }

  instantiatedInSection(key: string): InstantiatedEquipmentItem[] {
    const query = this.instantiatedSearchQuery.toLowerCase();
    return this.instantiatedEquipment.filter(item => {
      const label = this.equipmentTypeLabel(item.iconCode);
      const itemSection = Object.keys(this.sectionItems).find(k => this.sectionItems[k].includes(label)) ?? 'other';
      return itemSection === key && (!query || item.name.toLowerCase().includes(query) || label.toLowerCase().includes(query));
    });
  }

  isEquipmentPlaced(cimEquipmentId: number): boolean {
    const arr = this.diagram?.model?.nodeDataArray as any[] | undefined;
    return !!arr?.some(n => n['cimEquipmentId'] === cimEquipmentId);
  }

  loadInstantiatedEquipment(alreadyOnCanvas: Set<number> = new Set()): void {
    if (!this.substationId) return;
    this.equipmentListService.getInstantiatedBySubstationId(this.substationId).subscribe({
      next: (response: any) => {
        const all: InstantiatedEquipmentItem[] = response?.body?.data ?? [];
        this.cimTerminalsByEquipmentId = new Map(all.map(item => [item.id, item.terminals ?? []]));
        this.hubConnectivityNodeIds = new Set(all
          .flatMap(item => item.terminals ?? [])
          .filter(t => t.sequenceNumber === 99 && t.connectivityNodeId != null)
          .map(t => t.connectivityNodeId!));
        this.destinationCountByEquipmentId = new Map(all
          .filter(item => (item.destinationSubstationCount ?? 0) > 0)
          .map(item => [item.id, item.destinationSubstationCount!]));
        this.instantiatedEquipment = all.filter(item => !alreadyOnCanvas.has(item.id));
      },
      error: () => this.toastr.error('Error occurred while fetching instantiated equipment'),
    });
  }

  onDragInstantiated(event: DragEvent, item: InstantiatedEquipmentItem): void {
    if (this.isLocked || this.isEquipmentPlaced(item.id)) {
      event.preventDefault();
      return;
    }
    event.dataTransfer?.setData('text/plain', 'CIM:' + item.id);
  }

  private warnIfUnplacedInstantiatedEquipment(): boolean {
    const unplaced = this.instantiatedEquipment.filter(item => !this.isEquipmentPlaced(item.id)).length;
    if (!unplaced) return false;
    this.toastr.warning(`All instantiated equipment must be placed on the schema before publishing (${unplaced} remaining)`);
    return true;
  }

  private warnIfUnnamedEquipment(): boolean {
    const model = this.diagram.model;
    const unnamed = model.nodeDataArray.filter((n: any) =>
      SYMBOLS[n['category']] && !(model.findNodeDataForKey(n['labelKey'])?.['text'] as string | undefined)?.trim()).length;
    if (!unnamed) return false;
    this.toastr.warning(`All equipment must have a name before publishing (${unnamed} without a name)`);
    return true;
  }

  private validateAgainstCim(on: 'load' | 'publish', onDone?: (issues: SchematicValidationIssue[] | null) => void): void {
    const substationId = this.substationId;
    if (!substationId) return;
    const model = this.diagram.model as go.GraphLinksModel;
    const nodes: SchematicValidationNodeDataIn[] = model.nodeDataArray
      .filter((n: any) => Object.keys(SYMBOLS).includes(n['category'] as string))
      .map((n: any) => ({
        key: n['key'] as string,
        cimEquipmentId: (n['cimEquipmentId'] as number) ?? null,
        sourceAcLineId: (n['sourceAcLineId'] as number) ?? null,
        name: (n['labelKey'] ? model.findNodeDataForKey(n['labelKey'])?.['text'] as string : null) ?? null,
        type: n['category'] as string,
      }));

    const failed = (message?: string) => {
      this.toastr.error(message || 'Failed to check the schematic against CIM');
      onDone?.(null);
    };
    this.equipmentListService.validateSchematic({ substationId, nodes }).subscribe({
      next: (res) => {
        if (this.substationId !== substationId) return;
        if (res.body?.status !== 'OK') { failed(res.body?.message); return; }
        const issues = (res.body.data ?? []).filter(i => on === 'load' || i.severity === SchematicValidationSeverity.Error);
        this.applyCimIssues(issues, on === 'load' && this.isPublished);
        this.cdr.detectChanges();
        onDone?.(issues);
      },
      error: () => failed(),
    });
  }

  private applyCimIssues(issues: SchematicValidationIssue[], adoptCimNames: boolean): void {
    const model = this.diagram.model as go.GraphLinksModel;
    const errorsByKey = new Map<string, string[]>();
    const infosByKey = new Map<string, string[]>();
    const add = (map: Map<string, string[]>, key: string, message: string) => map.set(key, [...(map.get(key) ?? []), message]);
    this.cimSubstationIssue = issues.filter(i => i.nodeKey == null).map(i => i.message).join(' ') || null;

    withoutUndo(this.diagram, () => {
      for (const issue of issues) {
        if (issue.nodeKey == null) continue;
        if (issue.severity === SchematicValidationSeverity.Error) { add(errorsByKey, issue.nodeKey, issue.message); continue; }
        const nodeData = model.findNodeDataForKey(issue.nodeKey);
        const labelData = nodeData?.['labelKey'] ? model.findNodeDataForKey(nodeData['labelKey']) : null;
        if (adoptCimNames && issue.cimName != null && labelData) {
          add(infosByKey, issue.nodeKey,
            `'${labelData['text']}' was renamed to '${issue.cimName}' in CIM. The schematic now shows the new name; republish to save it.`);
          model.setDataProperty(labelData, 'text', issue.cimName);
        } else {
          add(infosByKey, issue.nodeKey, issue.message);
        }
      }

      model.nodeDataArray.forEach((n: any) => {
        if (!Object.keys(SYMBOLS).includes(n['category'] as string)) return;
        const error = errorsByKey.get(n['key'] as string)?.join(' ');
        const info = infosByKey.get(n['key'] as string)?.join(' ');
        if (error !== n['cimIssue']) model.setDataProperty(n, 'cimIssue', error);
        if (info !== n['cimInfo']) model.setDataProperty(n, 'cimInfo', info);
      });
    });
    this.refreshCimNodeIssues();
  }

  dismissCimInfos(): void {
    const model = this.diagram.model;
    withoutUndo(this.diagram, () => {
      model.nodeDataArray.forEach((n: any) => {
        if (n['cimInfo']) model.setDataProperty(n, 'cimInfo', undefined);
      });
    });
    this.refreshCimNodeIssues();
  }

  private refreshCimNodeIssues(): void {
    const nodes = this.diagram.model.nodeDataArray;
    this.cimNodeIssues = nodes.filter((n: any) => n['cimIssue']).map((n: any) => n['cimIssue'] as string);
    this.cimNodeInfos = nodes.filter((n: any) => n['cimInfo']).map((n: any) => n['cimInfo'] as string);
  }

  constructor(
    private cdr: ChangeDetectorRef,
    private schematicService: SchematicService,
    private cimSubstationService: CimSubstationService,
    private equipmentListService: EquipmentListService,
    private toastr: ToastrService,
    private sanitizer: DomSanitizer,
    private permissionsService: PermissionsService,
  ) {}

  showIncomingLinePanel = false;
  incomingParentSubstations: any[] = [];
  incomingEntries: { parentSubstation: any | null; acLineOptions: any[]; selectedAcLines: any[] }[] = [];

  openIncomingLinePanel(): void {
    if (this.isLocked) return;
    if (this.panelsCollapsed) this.togglePanels();
    this.closePanel();
    this.showIncomingLinePanel = true;
    this.incomingEntries = [this.newIncomingEntry()];
    this.loadIncomingParentSubstations('');
  }

  private newIncomingEntry() {
    return { parentSubstation: null, acLineOptions: [], selectedAcLines: [] };
  }

  onIncomingParentSubstationSearch(filterText: string): void {
    this.loadIncomingParentSubstations(filterText);
  }

  private loadIncomingParentSubstations(filterText: string): void {
    this.cimSubstationService.getForSelect(new SelectSubstationDataIn(filterText)).subscribe({
      next: (res: any) => {
        const body = res.body;
        this.incomingParentSubstations = (body?.status === 200 || body?.status === 'OK') ? (body.data ?? []) : [];
        this.cdr.detectChanges();
      },
      error: () => this.toastr.error('Error occurred while loading parent substations'),
    });
  }

  closeIncomingLinePanel(): void {
    this.showIncomingLinePanel = false;
  }

  availableParentSubstations(entry: { parentSubstation: any | null }): any[] {
    return this.incomingParentSubstations.filter(s =>
      s.id === entry.parentSubstation?.id ||
      !this.incomingEntries.some(e => e !== entry && e.parentSubstation?.id === s.id)
    );
  }

  onIncomingParentSubstationSelected(entry: { parentSubstation: any | null; acLineOptions: any[]; selectedAcLines: any[] }, substation: any | null): void {
    entry.parentSubstation = substation;
    entry.acLineOptions = [];
    entry.selectedAcLines = [];
    if (!substation) return;
    this.cimSubstationService.getOutgoingLines(substation.id).subscribe({
      next: (res: any) => {
        const body = res.body;
        entry.acLineOptions = (body?.status === 200 || body?.status === 'OK') ? (body.data ?? []) : [];
        this.cdr.detectChanges();
      },
      error: () => this.toastr.error('Error occurred while loading AC Lines for the selected substation'),
    });
  }

  onIncomingAcLinesSelected(entry: { selectedAcLines: any[] }, acLines: any[]): void {
    entry.selectedAcLines = acLines;
  }

  hasIncomingSelection(entry: { parentSubstation: any | null; selectedAcLines: any[] }): boolean {
    return !!entry.parentSubstation && entry.selectedAcLines.length > 0;
  }

  addAnotherIncomingSubstation(): void {
    this.incomingEntries.push(this.newIncomingEntry());
  }

  removeIncomingEntry(entry: unknown): void {
    this.incomingEntries = this.incomingEntries.filter(e => e !== entry);
    if (!this.incomingEntries.length) this.incomingEntries.push(this.newIncomingEntry());
  }

  get hasAnyIncomingAcLineSelected(): boolean {
    return this.incomingEntries.some(e => e.selectedAcLines.length > 0);
  }

  confirmAddIncomingAcLines(): void {
    const allSelected = this.incomingEntries.flatMap(e => e.selectedAcLines);
    if (!allSelected.length) { this.closeIncomingLinePanel(); return; }
    const model = this.diagram.model as go.GraphLinksModel;

    const occupied: go.Rect[] = [];
    this.diagram.nodes.each((n: go.Node) => { if (n.category !== 'LABEL') occupied.push(n.actualBounds.copy()); });

    model.startTransaction('add incoming ac lines');
    const addedKeys = allSelected.map(acLine => {
      const pt = this.findFreeIncomingLineSpot(occupied);
      return this.addIncomingAcLineNode(acLine, pt);
    });
    model.commitTransaction('add incoming ac lines');
    this.closeIncomingLinePanel();

    const addedNodes = addedKeys.map(k => this.diagram.findNodeForKey(k)).filter((n): n is go.Node => !!n);
    if (addedNodes.length) this.diagram.scrollToRect(this.diagram.computePartsBounds(addedNodes));
  }

  private findFreeIncomingLineSpot(occupied: go.Rect[]): go.Point {
    const sym = SYMBOLS['LN'];
    const stepY = 100;
    let y = 80;
    let pt = new go.Point(80, y);
    let rect = new go.Rect(pt.x - sym.w / 2, pt.y - sym.h / 2, sym.w, sym.h);
    while (occupied.some(r => r.intersectsRect(rect))) {
      y += stepY;
      pt = new go.Point(80, y);
      rect = new go.Rect(pt.x - sym.w / 2, pt.y - sym.h / 2, sym.w, sym.h);
    }
    occupied.push(rect);
    return pt;
  }

  private addIncomingAcLineNode(acLine: { id: number; name: string }, pt: go.Point): string {
    const sym = SYMBOLS['LN'];
    const symKey = 'n_' + Date.now() + '_' + acLine.id;
    const lblKey = 'lbl_' + symKey;
    (this.diagram.model as go.GraphLinksModel).addNodeData({
      key: symKey,
      category: 'LN',
      labelKey: lblKey,
      loc: go.Point.stringify(pt),
      sourceAcLineId: acLine.id,
    });
    (this.diagram.model as go.GraphLinksModel).addNodeData({
      key: lblKey,
      category: 'LABEL',
      master: symKey,
      text: acLine.name,
      loc: go.Point.stringify(new go.Point(pt.x, pt.y + sym.h / 2 + 12)),
    });
    return symKey;
  }

  ngAfterViewInit(): void {
    setTimeout(() => {
      this.initDiagram();
      this.setupDrop();
      this.loadSchematic();
    });
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['substationId'] && !changes['substationId'].firstChange && this.diagram) {
      this.resetForSubstationChange();
      this.loadSchematic();
    }
  }

  private resetForSubstationChange(): void {
    if (this._autoSaveTimer) { clearTimeout(this._autoSaveTimer); this._autoSaveTimer = null; }
    this._diagramReady = false;
    this.closePanel();
    this.showIncomingLinePanel = false;
    this.showExitConfirm = false;
    this.pendingLeave = null;
    this.schematicStatus = null;
    this.isPublished = false;
    this.isDraft = false;
    this.isPrePublish = false;
    this.isRejected = false;
    this.editMode = false;
    this.editModeDirty = false;
    this.lastKnownUpdateTime = null;
    this.schematicLoaded = false;
    this.diagramStatus = DiagramStatus.Empty;
    this.instantiatedEquipment = [];
    this.legendEntries = [];
    this.cimSubstationIssue = null;
    this.cimNodeIssues = [];
    this.cimNodeInfos = [];
    this.publishing = false;
    this.diagram.clear();
    this.syncUndoRedo();
  }

  toggleSection(key: string): void {
    this.sections[key] = !this.sections[key];
  }

  onDrag(event: DragEvent, type: string): void {
    if (this.isLocked) { event.preventDefault(); return; }
    event.dataTransfer?.setData('text/plain', type);
  }

  undo(): void { this.diagram.commandHandler.undo(); this.syncUndoRedo(); }
  redo(): void { this.diagram.commandHandler.redo(); this.syncUndoRedo(); }

  private syncUndoRedo(): void {
    const um = this.diagram.undoManager;
    this.canUndo = um.canUndo();
    this.canRedo = um.canRedo();
    this.cdr.detectChanges();
  }

  zoomIn(): void {
    const next = Math.min(Math.round(this.diagram.scale * 100) + 20, 400);
    this.diagram.scale = next / 100;
    this.zoomPercent = next;
  }

  zoomOut(): void {
    const next = Math.max(Math.round(this.diagram.scale * 100) - 20, 10);
    this.diagram.scale = next / 100;
    this.zoomPercent = next;
  }

  panelsCollapsed = false;
  // The palette slides away while the canvas is locked (nothing in it can be used). Set by applyLockState, not bound
  // to isLocked, so it starts hidden and doesn't flicker while a schematic loads.
  paletteHidden = true;

  togglePanels(): void {
    this.panelsCollapsed = !this.panelsCollapsed;
    setTimeout(() => this.fitToScreen(true), 280);
  }

  fitToScreen(animate: boolean = false): void {
    const b = this.diagram.documentBounds;
    const pad = 60;
    const rect = new go.Rect(b.x - pad, b.y - pad, b.width + pad * 2, b.height + pad * 2);
    const startScale = this.diagram.scale;
    const startPosition = this.diagram.position.copy();
    this.diagram.zoomToRect(rect);
    const vb = this.diagram.viewportBounds;
    const endPosition = new go.Point(
      rect.x + (rect.width - vb.width) / 2,
      rect.y + (rect.height - vb.height) / 2
    );
    const endScale = this.diagram.scale;
    if (animate) {
      this.diagram.scale = startScale;
      this.diagram.position = startPosition;
      new go.Animation({ duration: 280, easing: go.Animation.EaseOutExpo })
        .add(this.diagram, 'scale', startScale, endScale)
        .add(this.diagram, 'position', startPosition, endPosition)
        .start();
    } else {
      this.diagram.position = endPosition;
    }
    this.zoomPercent = Math.round(endScale * 100);
  }

  closePanel(): void {
    this.selectedNode = null;
    this.selectedLink = null;
    this.diagram?.clearSelection();
  }

  get annotationValue(): string { return this.annotationTag; }
  get activeColor(): string { return this.selectedNode ? this.selectedColor : this.linkColor; }

  onAnnotationInput(value: string): void {
    this.annotationTag = value;
    this.updateAnnotation();
  }

  onColorSelected(color: string): void {
    if (this.selectedNode) this.updateColor(color);
    else this.updateLinkColor(color);
  }

  onWipeSelection(): void {
    if (this.selectedNode) this.deleteSelection();
    else this.deleteLink();
  }

  trackByIndex(index: number): number { return index; }

  get topologyEntries(): TopologyEntry[] {
    if (this.selectedNode) {
      return this.nodeTopologyLinks.map(e => ({
        label: e.label,
        sublabel: e.sublabel,
        navigate: () => this.topologyNavigateToLink(e.linkData),
        disconnect: () => this.topologyDisconnect(e.linkData),
        navTitle: 'Go to link',
      }));
    }
    if (this.selectedLink) {
      return this.linkTopologyNodes.map(e => ({
        label: e.label,
        sublabel: e.sublabel,
        navigate: () => this.topologyNavigateToNode(e.nodeKey),
        disconnect: () => this.deleteLink(),
        navTitle: 'Go to node',
      }));
    }
    return [];
  }

  saveSchematic(isManual: boolean = false): void {
    if (this._autoSaveTimer) { clearTimeout(this._autoSaveTimer); this._autoSaveTimer = null; }
    const savedSubstationId = this.substationId;
    this.submitSchematicRequest<SchematicSaveDataOut>(
      this.schematicService.save(this.buildSavePayload()),
      'Failed to save schematic changes',
      (result) => {
        // The host may have switched to another substation while this save was in flight.
        if (this.substationId !== savedSubstationId) return;
        if (result?.lastUpdateTime) this.lastKnownUpdateTime = result.lastUpdateTime;
        if (result?.nodeMappings?.length) this.syncKeysAfterSave(result.nodeMappings);
        if (result?.status != null && result.status !== this.schematicStatus) {
          this.schematicStatus = result.status;
          this.isPublished = result.status === SchematicStatus.Published;
          this.isDraft = result.status === SchematicStatus.Draft;
          this.isPrePublish = result.status === SchematicStatus.PrePublish;
          this.isRejected = result.status === SchematicStatus.Rejected;
          this.resyncLabelVisibility();
          this.applyLockState();
          this.buildLegendEntries();
        }
        this.diagramStatus = this.isPublished ? DiagramStatus.Published : this.isPrePublish ? DiagramStatus.PrePublish : this.isRejected ? DiagramStatus.Rejected : DiagramStatus.Saved;
        if (isManual) this.toastr.success('Schematic draft successfully saved');
        this.cdr.detectChanges();
      }
    );
  }

  flushPendingSave(): void {
    if (!this._autoSaveTimer) return;
    clearTimeout(this._autoSaveTimer);
    this._autoSaveTimer = null;
    this.saveSchematic();
  }

  publishSchematic(closeAfter: boolean = false): void {
    const substationId = this.substationId;
    if (!substationId || this.warnIfUnplacedInstantiatedEquipment()) { this.publishing = false; return; }
    // The published image is always dark, so stored images look the same whichever theme the publisher used.
    this.renderDiagramSnapshot('image/png', (result: string) => {
      const image = result.substring(result.indexOf(',') + 1);
      this.submitSchematicRequest(
        this.schematicService.publish(substationId, image),
        'Failed to submit schematic for publishing',
        () => {
          this.publishing = false;
          this.schematicStatus = SchematicStatus.PrePublish;
          this.isPublished = false;
          this.isDraft = false;
          this.isPrePublish = true;
          this.editMode = false;
          this.editModeDirty = false;
          this.applyLockState();
          this.diagramStatus = DiagramStatus.PrePublish;
          this.toastr.info('Schematic submitted for publishing');
          if (closeAfter) { this.leave(); return; }
          this.cdr.detectChanges();
        },
        () => (this.publishing = false)
      );
    }, true);
  }

  saveAndPublish(closeAfter: boolean = false): void {
    if (this.publishing || !this.substationId || this.hasCimIssues || this.warnIfUnplacedInstantiatedEquipment() || this.warnIfUnnamedEquipment()) return;
    this.publishing = true;
    this.closeIncomingLinePanel();
    this.validateAgainstCim('publish', (issues) => {
      if (!issues) { this.publishing = false; return; }
      const errors = issues.filter(i => i.severity === SchematicValidationSeverity.Error);
      if (errors.length) {
        this.publishing = false;
        this.toastr.error(errors.map(i => i.message).join(' '), 'Cannot publish');
        return;
      }
      if (this._autoSaveTimer) { clearTimeout(this._autoSaveTimer); this._autoSaveTimer = null; }
      this.submitSchematicRequest<SchematicSaveDataOut>(
        this.schematicService.save(this.buildSavePayload()),
        'Failed to save changes before publishing',
        (result) => {
          if (result?.lastUpdateTime) this.lastKnownUpdateTime = result.lastUpdateTime;
          if (result?.nodeMappings?.length) this.syncKeysAfterSave(result.nodeMappings);
          this.publishSchematic(closeAfter);
        },
        () => (this.publishing = false)
      );
    });
  }

  private submitSchematicRequest<T>(request$: Observable<any>, errorMessage: string, onOk: (data: T | null) => void, onFail?: () => void): void {
    request$.subscribe({
      next: (res: any) => {
        if (res.body?.status !== 'OK') {
          onFail?.();
          this.toastr.error(res.body?.message || errorMessage);
          this.cdr.detectChanges();
          return;
        }
        onOk(res.body?.data ?? null);
      },
      error: () => {
        onFail?.();
        this.toastr.error(errorMessage);
        this.cdr.detectChanges();
      },
    });
  }

  showExitConfirm = false;
  // Set while the exit dialog guards a host action (e.g. switching substation) instead of closing.
  private pendingLeave: (() => void) | null = null;

  confirmRepublishAndExit(): void {
    this.showExitConfirm = false;
    this.saveAndPublish(true);
  }

  confirmDiscardAndExit(): void {
    this.showExitConfirm = false;
    this.editMode = false;
    this.applyLockState();
    this.leave();
  }

  cancelExit(): void {
    this.showExitConfirm = false;
    this.pendingLeave = null;
  }

  // For hosts that move this schematic to another substation: runs onLeave once nothing would be lost,
  // asking first (same dialog as closing) when a published schematic has unrepublished edits.
  requestLeave(onLeave: () => void): void {
    if (this.hasUnpublishedEditChanges) {
      this.pendingLeave = onLeave;
      this.showExitConfirm = true;
      return;
    }
    this.flushPendingSave();
    onLeave();
  }

  private leave(): void {
    const onLeave = this.pendingLeave;
    this.pendingLeave = null;
    if (onLeave) onLeave();
    else this.closeSchematic.emit();
  }

  onExitConfirmBackdropClick(event: MouseEvent): void {
    if (event.target === event.currentTarget) this.cancelExit();
  }

  onCloseSchematic(): void {
    if (this.hasUnpublishedEditChanges) {
      this.showExitConfirm = true;
      return;
    }
    this.closeSchematic.emit();
  }

  @HostListener('window:beforeunload', ['$event'])
  warnBeforeUnload(event: BeforeUnloadEvent): void {
    if (this.hasUnpublishedEditChanges) {
      event.preventDefault();
      event.returnValue = true;
    }
  }

  private buildSavePayload(): SchematicSaveRequest {
    const model = this.diagram.model as go.GraphLinksModel;
    const nodeMap = new Map<any, any>();
    model.nodeDataArray.forEach((n: any) => nodeMap.set(n['key'], n));

    const nodes: SchematicNodeDataIn[] = model.nodeDataArray
      .filter((n: any) => Object.keys(SYMBOLS).includes(n['category'] as string))
      .map((n: any) => {
        const parts = n['loc'] ? (n['loc'] as string).split(' ') : ['0', '0'];
        const labelNode = n['labelKey'] ? nodeMap.get(n['labelKey']) : null;
        const lblParts = labelNode?.['loc'] ? (labelNode['loc'] as string).split(' ') : [null, null];
        return {
          gojsKey: n['key'] as string,
          iconCode: n['category'] as string,
          label: (labelNode?.['text'] as string) ?? null,
          cimEquipmentId: (n['cimEquipmentId'] as number) ?? null,
          sourceAcLineId: (n['sourceAcLineId'] as number) ?? null,
          isClosed: ['CB', 'DS', 'LBS', 'GD', 'REC', 'SEC'].includes(n['category'] as string)
            ? (n['state'] != null ? (n['state'] as string) === 'closed' : null)
            : null,
          scale: (n['nodeScale'] as number) ?? 1.0,
          length: (n['length'] as number) ?? null,
          colorCode: (n['color'] as string) ?? null,
          coordinateX: parts[0] ?? null,
          coordinateY: parts[1] ?? null,
          labelCoordinateX: lblParts[0] ?? null,
          labelCoordinateY: lblParts[1] ?? null,
          angle: (n['angle'] as number) ?? null,
          labelAngle: (labelNode?.['labelAngle'] as number) ?? null,
        };
      });

    const edges: SchematicEdgeDataIn[] = model.linkDataArray.map((l: any) => {
      return {
        sourceGojsKey: l['from'] as string,
        destinationGojsKey: l['to'] as string,
        colorCode: (l['color'] as string) ?? null,
        fromPort: (l['fromPort'] as string) || null,
        toPort: (l['toPort'] as string) || null,
        busbarFrom: (l['busbarFrom'] as number) ?? null,
        busbarTo: (l['busbarTo'] as number) ?? null,
      };
    });

    return { substationId: this.substationId, nodes, edges, expectedLastUpdateTime: this.lastKnownUpdateTime };
  }

  private syncKeysAfterSave(mappings: NonNullable<SchematicSaveDataOut['nodeMappings']>): void {
    this._diagramReady = false;
    const m = this.diagram.model as go.GraphLinksModel;
    this.diagram.skipsUndoManager = true;
    m.startTransaction('sync keys after save');
    for (const mapping of mappings) {
      const newKey = mapping.dbId.toString();
      // A numeric key can still need remapping: undoing the delete of a saved node brings back its old id,
      // which the save no longer finds live, so it was inserted as a new row.
      if (mapping.gojsKey === newKey || m.findNodeDataForKey(newKey)) continue;
      const symData = m.findNodeDataForKey(mapping.gojsKey);
      if (!symData) continue;
      const newLabelKey = 'lbl_' + mapping.dbId;
      const oldLabelKey = symData['labelKey'] as string | undefined;
      if (oldLabelKey) {
        const lblData = m.findNodeDataForKey(oldLabelKey);
        if (lblData) {
          m.setDataProperty(lblData, 'master', newKey);
          m.setKeyForNodeData(lblData, newLabelKey);
        }
        m.setDataProperty(symData, 'labelKey', newLabelKey);
      }
      m.setKeyForNodeData(symData, newKey);
    }
    m.commitTransaction('sync keys after save');
    this.diagram.skipsUndoManager = false;
    this._diagramReady = true;
  }

  private markChanged(): void {
    if (!this.substationId || !this._diagramReady) return;
    if (this.isPublished) {
      if (this.editMode) this.editModeDirty = true;
      return;
    }
    if (this.isPrePublish) return;
    this.diagramStatus = DiagramStatus.Changed;
    this.cdr.detectChanges();
    if (this._autoSaveTimer) clearTimeout(this._autoSaveTimer);
    this._autoSaveTimer = setTimeout(() => {
      this._autoSaveTimer = null;
      this.saveSchematic();
    }, 2000);
  }

  private resyncPortVisuals(): void {
    withoutUndo(this.diagram, () => {
      this.diagram.nodes.each((node: go.Node) => {
        if (['LABEL', 'BB'].includes(node.data?.category)) return;
        const isLine = node.data?.category === 'LN';
        ['T', 'R', 'B', 'L'].forEach(pid => {
          const p = node.findPort(pid) as go.Shape;
          if (!(p instanceof go.Shape)) return;

          let linkColor: string | null = null;
          node.findLinksConnected().each((lnk: go.Link) => {
            const onThisPort =
              (lnk.fromNode === node && (lnk.data as any)?.fromPort === pid) ||
              (lnk.toNode   === node && (lnk.data as any)?.toPort   === pid);
            if (onThisPort) linkColor = (lnk.data as any)?.color as string || '#ffffff';
          });

          if (linkColor) {
            p.fill = themeColor(linkColor, this.drawLight);
            p.opacity = 1;
          } else if (isLine) {
            p.fill = themeColor('#ffffff', this.drawLight);
            p.opacity = 1;
          } else {
            p.fill = '#22c55e';
            p.opacity = 0;
          }
        });
      });
    });
  }

  private resyncLabelVisibility(): void {
    withoutUndo(this.diagram, () => {
      this.diagram.nodes.each((node: go.Node) => {
        const labelKey = node.data?.labelKey;
        if (!labelKey) return;
        const lbl = this.diagram.findNodeForKey(labelKey);
        if (!lbl) return;
        const hidden = this.isPublished && node.data?.category !== 'LN';
        (this.diagram.model as go.GraphLinksModel).setDataProperty(lbl.data, 'hiddenUntilHover', hidden);
      });
    });
  }

  // GoJS records Diagram property changes in the undo history (folded into the next transaction when none is open),
  // so without this an undo or redo would also lock or unlock the canvas.
  private applyLockState(): void {
    const editable = !this.isLocked;
    withoutUndo(this.diagram, () => {
      this.diagram.allowMove = editable;
      // Copy too: with it on, GoJS still starts a (no-op) drag on a locked canvas and commits its transaction,
      // which would mark a draft CHANGED and auto-save it.
      this.diagram.allowCopy = editable;
      this.diagram.allowLink = editable;
      this.diagram.allowRelink = editable;
      this.diagram.allowDelete = editable;
      this.diagram.allowInsert = editable;
      this.diagram.allowUndo = editable;
    });
    if (this.paletteHidden !== this.isLocked) {
      this.paletteHidden = this.isLocked;
      // Same as togglePanels: refit once the palette's slide has finished.
      setTimeout(() => this.fitToScreen(true), 280);
    }
  }

  toggleLegend(): void {
    this.legendOpen = !this.legendOpen;
  }

  private buildLegendEntries(): void {
    const seen = new Set<string>();
    const entries: Array<{ name: string; iconSvg: SafeHtml }> = [];
    this.diagram.nodes.each((node: go.Node) => {
      const category = node.data?.category as string;
      const sym = SYMBOLS[category];
      if (!sym) return;
      const state = (node.data?.state as string) || 'closed';
      const isStateful = sym.gen('#ffffff', 'open', sym.w) !== sym.gen('#ffffff', 'closed', sym.w);
      const key = isStateful ? `${category}:${state}` : category;
      if (seen.has(key)) return;
      seen.add(key);

      const baseName = (this.symbolLabels[category] ?? category)
        .toLowerCase().replace(/\b\w/g, c => c.toUpperCase());
      const name = isStateful ? `${baseName} (${state === 'open' ? 'Open' : 'Closed'})` : baseName;
      entries.push({ name, iconSvg: this.legendIconSvg(category, state) });
    });
    this.legendEntries = entries;
  }

  private legendIconSvg(category: string, state: string): SafeHtml {
    const sym = SYMBOLS[category];
    const w = category === 'BB' ? 60 : sym.w;
    const h = category === 'LN' ? 40 : sym.h;
    const length = category === 'BB' ? 60 : category === 'LN' ? 40 : undefined;
    const svg = sym.gen(themeColor('#ffffff', this.isLightTheme), state, length, this.isLightTheme ? '#e4dfdf' : undefined);
    const markup = `<svg viewBox="0 0 ${w} ${h}" width="100%" height="100%" xmlns="http://www.w3.org/2000/svg">${svg}</svg>`;
    return this.sanitizer.bypassSecurityTrustHtml(markup);
  }

  private loadSchematic(): void {
    if (!this.substationId) return;
    const requestedSubstationId = this.substationId;
    this._diagramReady = false;

    this.schematicService.getBySubstationId(this.substationId).subscribe((res: any) => {
      if (this.substationId !== requestedSubstationId) return;

      const schematic: SchematicDataOut | null = res.body?.data;
      if (!schematic) {
        this._diagramReady = true;
        this.schematicLoaded = true;
        this.diagramStatus = DiagramStatus.Empty;
        this.applyLockState();
        this.openIncomingLinePanel();
        if (!this.viewOnly) this.loadInstantiatedEquipment();
        this.cdr.detectChanges();
        return;
      }

      this.lastKnownUpdateTime = schematic.lastUpdateTime ?? null;
      this.schematicStatus = schematic.status ?? null;
      this.isPublished = this.schematicStatus === SchematicStatus.Published;
      this.isDraft = this.schematicStatus === SchematicStatus.Draft;
      this.isPrePublish = this.schematicStatus === SchematicStatus.PrePublish;
      this.isRejected = this.schematicStatus === SchematicStatus.Rejected;
      this.applyLockState();

      if (!this.viewOnly) {
        const alreadyOnCanvas = new Set<number>(
          schematic.nodes.filter(n => n.cimEquipmentId != null).map(n => n.cimEquipmentId as number)
        );
        this.loadInstantiatedEquipment(alreadyOnCanvas);
      }

      const nodeDataArray: any[] = [];
      const linkDataArray: any[] = [];

      for (const node of schematic.nodes) {
        const sym = SYMBOLS[node.iconCode ?? ''];
        const lblX = node.labelCoordinateX ?? node.coordinateX;
        const lblY = node.labelCoordinateY ?? String(parseFloat(node.coordinateY ?? '0') + (sym ? sym.h / 2 + 12 : 24));

        nodeDataArray.push({
          key: node.id.toString(),
          category: node.iconCode,
          labelKey: 'lbl_' + node.id,
          loc: `${node.coordinateX} ${node.coordinateY}`,
          color: node.colorCode,
          nodeScale: node.scale,
          state: node.isClosed != null ? (node.isClosed ? 'closed' : 'open') : undefined,
          length: node.length ?? undefined,
          angle: node.angle ?? undefined,
          sourceAcLineId: node.sourceAcLineId ?? undefined,
          cimEquipmentId: node.cimEquipmentId ?? undefined,
        });

        nodeDataArray.push({
          key: 'lbl_' + node.id,
          category: 'LABEL',
          master: node.id.toString(),
          text: node.label ?? node.iconCode,
          loc: `${lblX} ${lblY}`,
          labelAngle: node.labelAngle ?? undefined,
          hiddenUntilHover: this.isPublished && node.iconCode !== 'LN',
        });
      }

      for (const edge of schematic.edges) {
        const linkData: any = {
          from: edge.sourceNodeId.toString(),
          to: edge.destinationNodeId.toString(),
          color: edge.colorCode ?? '#ffffff',
        };
        if (edge.fromPort) linkData['fromPort'] = edge.fromPort;
        if (edge.toPort) linkData['toPort'] = edge.toPort;
        if (edge.busbarFrom != null) linkData['busbarFrom'] = edge.busbarFrom;
        if (edge.busbarTo != null) linkData['busbarTo'] = edge.busbarTo;
        linkDataArray.push(linkData);
      }

      const newModel = new go.GraphLinksModel({
        linkFromPortIdProperty: 'fromPort',
        linkToPortIdProperty: 'toPort',
        nodeDataArray,
        linkDataArray,
      });

      this.diagram.model = newModel;

      const onInitialLayout = () => {
        this.diagram.removeDiagramListener('InitialLayoutCompleted', onInitialLayout);
        this.fitToScreen();
        this.resyncPortVisuals();
        this.resyncLabelVisibility();
        this.buildLegendEntries();
        this.diagram.skipsUndoManager = true;
        this.diagram.links.each((l: go.Link) => {
          l.updateTargetBindings();
          l.invalidateRoute();
        });
        setTimeout(() => { this.diagram.skipsUndoManager = false; });
      };
      this.diagram.addDiagramListener('InitialLayoutCompleted', onInitialLayout);

      this.diagram.model.addChangedListener((e: go.ChangedEvent) => {
        if (e.change === go.ChangeType.Transaction &&
            (e.propertyName === 'CommittedTransaction' ||
             e.propertyName === 'FinishedUndo' ||
             e.propertyName === 'FinishedRedo')) {
          this.syncUndoRedo();
          this.refreshCimNodeIssues();
          this.markChanged();
        }
        // Undo/redo also restores the raw drawn values (symbol images, strokes) recorded when the edit was made,
        // which are in the theme active back then, so redraw everything from the model in the current theme.
        if (e.change === go.ChangeType.Transaction &&
            (e.propertyName === 'FinishedUndo' || e.propertyName === 'FinishedRedo')) {
          this.applyCanvasTheme(this.drawLight);
        }
      });

      if (this._autoSaveTimer) { clearTimeout(this._autoSaveTimer); this._autoSaveTimer = null; }
      this.diagramStatus = this.isPublished ? DiagramStatus.Published : this.isPrePublish ? DiagramStatus.PrePublish : this.isRejected ? DiagramStatus.Rejected : DiagramStatus.Saved;
      this.schematicLoaded = true;
      if (schematic.nodes.length === 0 && this.isDraft) this.openIncomingLinePanel();
      this.syncUndoRedo();
      this.cdr.detectChanges();
      if (schematic.nodes.length > 0) this.validateAgainstCim('load');
      setTimeout(() => { this._diagramReady = true; }, 300);
    });
  }

  updateAnnotation(): void {
    if (!this.selectedNode) return;
    const lblNode = this.selectedNode.data.labelKey
      ? this.diagram.findNodeForKey(this.selectedNode.data.labelKey) : null;
    if (!lblNode) return;
    this.diagram.model.startTransaction('rename');
    this.diagram.model.setDataProperty(lblNode.data, 'text', this.annotationTag);
    this.diagram.model.commitTransaction('rename');
  }

  updateColor(color: string): void {
    if (!this.selectedNode) return;
    this.selectedColor = color;
    this.diagram.model.startTransaction('color');
    this.diagram.model.setDataProperty(this.selectedNode.data, 'color', color);
    this.diagram.model.commitTransaction('color');
  }

  updateScale(): void {
    if (!this.selectedNode) return;
    const v = Math.max(0.1, +this.scaleValue.toFixed(1));
    this.scaleValue = v;
    this.diagram.model.startTransaction('scale');
    this.diagram.model.setDataProperty(this.selectedNode.data, 'nodeScale', v);
    this.diagram.model.commitTransaction('scale');
  }

  incrementScale(): void {
    this.scaleValue = +(this.scaleValue + 0.1).toFixed(1);
    this.updateScale();
  }

  decrementScale(): void {
    this.scaleValue = Math.max(0.1, +(this.scaleValue - 0.1).toFixed(1));
    this.updateScale();
  }

  onScaleKey(e: KeyboardEvent): void {
    if (this.isLocked) return;
    if (e.key === 'ArrowUp') { e.preventDefault(); this.incrementScale(); }
    if (e.key === 'ArrowDown') { e.preventDefault(); this.decrementScale(); }
  }

  updateNodeLength(): void {
    if (!this.selectedNode) return;
    const v = Math.max(1, this.nodeLength || 280);
    this.nodeLength = v;
    this.diagram.model.startTransaction('length');
    this.diagram.model.setDataProperty(this.selectedNode.data, 'length', v);
    this.diagram.model.commitTransaction('length');
  }

  incrementNodeLength(): void {
    this.nodeLength = (this.nodeLength || 280) + 10;
    this.updateNodeLength();
  }

  decrementNodeLength(): void {
    this.nodeLength = Math.max(1, (this.nodeLength || 2) - 10);
    this.updateNodeLength();
  }

  onNodeLengthKey(e: KeyboardEvent): void {
    if (this.isLocked) return;
    if (e.key === 'ArrowUp') { e.preventDefault(); this.incrementNodeLength(); }
    if (e.key === 'ArrowDown') { e.preventDefault(); this.decrementNodeLength(); }
  }

  toggleState(): void {
    if (!this.selectedNode) return;
    this.selectedState = this.selectedState === 'closed' ? 'open' : 'closed';
    this.diagram.model.startTransaction('state');
    this.diagram.model.setDataProperty(this.selectedNode.data, 'state', this.selectedState);
    this.diagram.model.commitTransaction('state');
  }

  pivotNode(): void {
    if (!this.selectedNode) return;
    const angle = ((this.selectedNode.data.angle as number || 0) + 90) % 360;
    this.diagram.model.startTransaction('pivot');
    this.diagram.model.setDataProperty(this.selectedNode.data, 'angle', angle);
    this.diagram.model.commitTransaction('pivot');
  }

  rotateLabel(): void {
    if (!this.selectedNode) return;
    const lblNode = this.selectedNode.data.labelKey
      ? this.diagram.findNodeForKey(this.selectedNode.data.labelKey) : null;
    if (!lblNode) return;
    const angle = ((lblNode.data.labelAngle as number || 0) + 90) % 360;
    this.diagram.model.startTransaction('rotate label');
    this.diagram.model.setDataProperty(lblNode.data, 'labelAngle', angle);
    this.diagram.model.commitTransaction('rotate label');
  }

  duplicateNode(): void {
    if (!this.selectedNode) return;
    this.diagram.model.startTransaction('duplicate');
    this.duplicate(this.selectedNode.data as Record<string, unknown>);
    this.diagram.model.commitTransaction('duplicate');
  }

  private copiedNodeData: Record<string, unknown>[] = [];
  private duplicateSeq = 0;

  private duplicate(d: Record<string, unknown>): void {
    const loc = go.Point.parse(d['loc'] as string);
    const newSymKey = `n_${Date.now()}_${++this.duplicateSeq}`;
    const newLblKey = 'lbl_' + newSymKey;

    const srcLbl = d['labelKey']
      ? this.diagram.findNodeForKey(d['labelKey'] as string) : null;
    const srcLblLoc = srcLbl
      ? go.Point.parse(srcLbl.data.loc as string)
      : new go.Point(loc.x, loc.y + 30);

    this.diagram.model.addNodeData({
      key: newSymKey,
      category: d['category'],
      color: d['color'],
      angle: d['angle'],
      nodeScale: d['nodeScale'],
      state: d['state'],
      length: d['length'],
      labelKey: newLblKey,
      loc: go.Point.stringify(new go.Point(loc.x + 70, loc.y + 30)),
    });
    this.diagram.model.addNodeData({
      key: newLblKey,
      category: 'LABEL',
      master: newSymKey,
      text: this.duplicateName((srcLbl?.data.text as string) || (d['category'] as string)),
      labelAngle: srcLbl?.data.labelAngle,
      loc: go.Point.stringify(new go.Point(srcLblLoc.x + 70, srcLblLoc.y + 30)),
      hiddenUntilHover: this.isPublished && d['category'] !== 'LN',
    });
  }

  private duplicateName(name: string): string {
    const base = name.replace(/ duplicate( \d+)?$/, '');
    const taken = new Set(this.diagram.model.nodeDataArray
      .filter((n: any) => n['category'] === 'LABEL')
      .map((n: any) => n['text'] as string));
    let candidate = `${base} duplicate`;
    for (let i = 2; taken.has(candidate); i++) candidate = `${base} duplicate ${i}`;
    return candidate;
  }

  private blockFeederDelete(node: go.Node): boolean {
    const count = this.destinationCountByEquipmentId.get(node.data?.cimEquipmentId);
    if (!count) return false;
    this.toastr.warning(`${this.nodeName(node)} belongs to an AC line that is a source to ${count} substation(s) ` +
      `and cannot be deleted until it is no longer a source to any substation`);
    return true;
  }

  deleteSelection(): void {
    if (!this.selectedNode) return;
    if (this.blockFeederDelete(this.selectedNode)) return;
    const lblKey = this.selectedNode.data.labelKey as string | undefined;
    const deletedNode = this.selectedNode;

    const portsToReset: Array<{ node: go.Node; pid: string }> = [];
    deletedNode.findLinksConnected().each((lnk: go.Link) => {
      const other = lnk.fromNode === deletedNode ? lnk.toNode : lnk.fromNode;
      const pid   = lnk.fromNode === deletedNode
        ? (lnk.data as any)?.toPort   as string
        : (lnk.data as any)?.fromPort as string;
      if (other && other !== deletedNode && ['T','R','B','L'].includes(pid))
        portsToReset.push({ node: other, pid });
    });

    this.selectedNode = null;

    this.diagram.model.startTransaction('wipe node');
    this.diagram.commandHandler.deleteSelection();

    portsToReset.forEach(({ node, pid }) => this.resetPort(node, pid));

    if (lblKey) {
      const lbl = this.diagram.findNodeForKey(lblKey);
      if (lbl) this.diagram.model.removeNodeData(lbl.data);
    }
    this.diagram.model.commitTransaction('wipe node');
  }

  updateLinkColor(color: string): void {
    if (!this.selectedLink) return;
    this.linkColor = color;
    this.diagram.model.startTransaction('link color');
    this.diagram.model.setDataProperty(this.selectedLink.data, 'color', color);
    const syncPort = (node: go.Node | null, pid: string) => {
      if (!node || !pid) return;
      const p = node.findPort(pid) as go.Shape;
      if (p instanceof go.Shape) p.fill = themeColor(color, this.drawLight);
    };
    syncPort(this.selectedLink.fromNode, (this.selectedLink.data as any).fromPort);
    syncPort(this.selectedLink.toNode,   (this.selectedLink.data as any).toPort);
    this.diagram.model.commitTransaction('link color');
  }

  deleteLink(): void {
    if (!this.selectedLink) return;
    const fromNode = this.selectedLink.fromNode;
    const toNode   = this.selectedLink.toNode;
    const fromPort = (this.selectedLink.data as any).fromPort as string;
    const toPort   = (this.selectedLink.data as any).toPort   as string;
    this.selectedLink = null;
    this.diagram.model.startTransaction('wipe link');
    this.diagram.commandHandler.deleteSelection();
    this.resetPort(fromNode, fromPort);
    this.resetPort(toNode,   toPort);
    this.diagram.model.commitTransaction('wipe link');
  }

  private resetPort(node: go.Node | null, pid: string): void {
    if (!node || !['T', 'R', 'B', 'L'].includes(pid)) return;
    const p = node.findPort(pid) as go.Shape;
    if (!(p instanceof go.Shape)) return;
    const stillLinked = node.findLinksConnected().any(
      (lnk: go.Link) =>
        (lnk.fromNode === node && (lnk.data as any)?.fromPort === pid) ||
        (lnk.toNode   === node && (lnk.data as any)?.toPort   === pid)
    );
    if (!stillLinked) {
      this.diagram.model.startTransaction('reset port');
      if (node.data?.category === 'LN') {
        p.fill = themeColor('#ffffff', this.drawLight);
        p.opacity = 1;
      } else {
        p.fill = '#22c55e';
        p.opacity = 0;
      }
      this.diagram.model.commitTransaction('reset port');
    }
  }

  private topologyDisconnect(linkData: any): void {
    if (!linkData) return;
    let targetLink: go.Link | null = null;
    this.diagram.links.each((lnk: go.Link) => { if (lnk.data === linkData) targetLink = lnk; });
    if (!targetLink) return;
    const resolvedLink = targetLink as go.Link;

    const isCurrentLink = this.selectedLink?.data === linkData;
    const model = this.diagram.model as go.GraphLinksModel;

    const fromNode = resolvedLink.fromNode;
    const toNode   = resolvedLink.toNode;
    const fromPort = (linkData as any).fromPort as string;
    const toPort   = (linkData as any).toPort   as string;

    this.diagram.model.startTransaction('disconnect');
    model.removeLinkData(linkData);
    this.diagram.model.commitTransaction('disconnect');

    this.resetPort(fromNode, fromPort);
    this.resetPort(toNode,   toPort);

    if (isCurrentLink) {
      this.selectedLink = null;
      this.linkTopologyNodes = [];
    } else {
      this.nodeTopologyLinks = this.nodeTopologyLinks.filter(e => e.linkData !== linkData);
    }
    this.cdr.detectChanges();
  }

  private topologyNavigateToLink(linkData: any): void {
    let found: go.Link | null = null;
    this.diagram.links.each((lnk: go.Link) => { if (lnk.data === linkData) found = lnk; });
    if (found) this.diagram.select(found);
  }

  private topologyNavigateToNode(nodeKey: any): void {
    const node = this.diagram.findNodeForKey(nodeKey);
    if (node) this.diagram.select(node);
  }

  private setupDrop(): void {
    const el = this.canvasWrapper.nativeElement;

    el.addEventListener('dragover', (e: DragEvent) => {
      e.preventDefault();
    }, true);

    el.addEventListener('drop', (e: DragEvent) => {
      e.preventDefault();
      e.stopPropagation();
      if (this.isLocked) return;

      const raw = e.dataTransfer?.getData('text/plain') ?? '';
      const isInstantiated = raw.startsWith('CIM:');
      const item = isInstantiated
        ? this.instantiatedEquipment.find(i => i.id === Number(raw.slice(4)))
        : undefined;
      if (isInstantiated && (!item || this.isEquipmentPlaced(item.id))) return;
      const type = item ? item.iconCode : raw;

      const div = this.diagram.div!;
      const rect = div.getBoundingClientRect();
      // The host app's anti-zoom (CSS zoom on :root) makes on-screen pixels differ from the canvas's CSS pixels,
      // which is what GoJS view coordinates are in.
      const zoom = rect.width / div.offsetWidth || 1;
      const pt = this.diagram.transformViewToDoc(
        new go.Point((e.clientX - rect.left) / zoom, (e.clientY - rect.top) / zoom)
      );

      const symKey = 'n_' + Date.now();
      const lblKey = 'lbl_' + Date.now();
      const sym = SYMBOLS[type as string];
      this.diagram.model.startTransaction('drop node');
      this.diagram.model.addNodeData({
        key: symKey,
        category: type,
        labelKey: lblKey,
        loc: go.Point.stringify(pt),
        ...(item ? {
          cimEquipmentId: item.id,
          sourceAcLineId: item.acLineType === 'Incoming' ? item.id : undefined,
        } : {}),
      });
      this.diagram.model.addNodeData({
        key: lblKey,
        category: 'LABEL',
        master: symKey,
        text: item ? item.name : (this.symbolLabels[type as string] ?? type),
        loc: go.Point.stringify(new go.Point(pt.x, pt.y + (sym ? sym.h / 2 + 12 : 24))),
        hiddenUntilHover: this.isPublished && type !== 'LN',
      });
      this.diagram.model.commitTransaction('drop node');

      if (item) this.linkExistingCimConnections(symKey, item.id);
    }, true);
  }

  // Draws a link to every placed equipment that shares a connectivity node with the dropped one in CIM, within the
  // same limits manual drawing has (isWithinLinkLimits). A user-drawn link holding a slot the CIM connection needs
  // is replaced by it; a slot held by another CIM connection is kept, and the new link is skipped instead.
  // All in one transaction, so a single undo puts everything back.
  private linkExistingCimConnections(nodeKey: string, cimEquipmentId: number): void {
    const node = this.diagram.findNodeForKey(nodeKey);
    if (!node || !this.cimTerminalsByEquipmentId.get(cimEquipmentId)?.length) return;

    const model = this.diagram.model as go.GraphLinksModel;
    const linked: string[] = [];
    const replaced: string[] = [];
    const skipped: string[] = [];
    model.startTransaction('link existing CIM connections');

    this.diagram.nodes.each((other: go.Node) => {
      if (!other.data?.cimEquipmentId || other === node || node.findLinksBetween(other).count > 0) return;

      const direction = this.cimDirection(node, other);
      if (!direction) return;
      const [from, to] = direction === 'forward' ? [node, other] : [other, node];

      const blocking = this.linksBlocking(from, to);
      if (!blocking) {
        skipped.push(this.nodeName(other));
        return;
      }
      blocking.forEach(link => {
        replaced.push(`${this.nodeName(link.fromNode!)} → ${this.nodeName(link.toNode!)}`);
        model.removeLinkData(link.data);
      });

      model.addLinkData({
        from: from.key,
        to: to.key,
        fromPort: this.nearestFreePort(from, to),
        toPort: this.nearestFreePort(to, from),
        color: '#ffffff',
        ...(from.data.category === 'BB' ? { busbarFrom: 0.5 } : {}),
        ...(to.data.category === 'BB' ? { busbarTo: 0.5 } : {}),
      });
      linked.push(this.nodeName(other));
    });

    model.commitTransaction('link existing CIM connections');
    if (linked.length) {
      this.resyncPortVisuals();
      this.toastr.info(`Connected to ${linked.join(', ')} (existing CIM connection)` +
        (replaced.length ? `, replacing ${replaced.join(', ')}` : ''));
    }
    if (skipped.length)
      this.toastr.info(`Not connected to ${skipped.join(', ')}: the connection is already taken by another existing CIM connection`);
  }

  // Which way a and b are connected in CIM: out (seq 2) feeds in (seq 1), a busbar/junction terminal (seq 99) connects
  // to either. Two ins or two outs on the same node are siblings, not connected to each other.
  private cimDirection(a: go.Node, b: go.Node): 'forward' | 'backward' | null {
    const aTerminals = this.cimTerminalsByEquipmentId.get(a.data?.cimEquipmentId) ?? [];
    const bTerminals = this.cimTerminalsByEquipmentId.get(b.data?.cimEquipmentId) ?? [];

    for (const mine of aTerminals) {
      const theirs = bTerminals.find(t => t.connectivityNodeId != null && t.connectivityNodeId === mine.connectivityNodeId);
      if (!theirs) continue;
      // Two non-hubs on a hub's node aren't connected to each other, only to the hub.
      if (mine.sequenceNumber !== 99 && theirs.sequenceNumber !== 99 && this.hubConnectivityNodeIds.has(mine.connectivityNodeId!))
        continue;
      if ((mine.sequenceNumber === 2 && theirs.sequenceNumber !== 2) || (mine.sequenceNumber === 99 && theirs.sequenceNumber !== 2))
        return 'forward';
      if ((mine.sequenceNumber === 1 && theirs.sequenceNumber !== 1) || (mine.sequenceNumber === 99 && theirs.sequenceNumber === 2))
        return 'backward';
    }
    return null;
  }

  // The user-drawn links that have to go for from → to to fit within its symbols' maxSources/maxDestinations,
  // or null if it can't be drawn: a busbar/junction pair, an incoming AC line as destination, or a full slot held
  // only by CIM connections.
  private linksBlocking(from: go.Node, to: go.Node): go.Link[] | null {
    const hubs = ['BB', 'JN'];
    if (hubs.includes(from.data?.category) && hubs.includes(to.data?.category)) return null;
    if (to.data?.sourceAcLineId != null) return null;

    const overLimit = (links: go.Link[], max: number | undefined): go.Link[] | null => {
      if (max === undefined || links.length < max) return [];
      const userDrawn = links.filter(l => this.cimDirection(l.fromNode!, l.toNode!) !== 'forward');
      const needed = links.length - max + 1;
      return userDrawn.length >= needed ? userDrawn.slice(0, needed) : null;
    };

    const linksWhere = (node: go.Node, pred: (l: go.Link) => boolean): go.Link[] => {
      const links: go.Link[] = [];
      node.findLinksConnected().each((l: go.Link) => { if (pred(l)) links.push(l); });
      return links;
    };

    const outgoing = overLimit(linksWhere(from, l => l.fromNode === from), SYMBOLS[from.data?.category]?.maxSources);
    const incoming = overLimit(linksWhere(to, l => l.toNode === to), SYMBOLS[to.data?.category]?.maxDestinations);
    if (!outgoing || !incoming) return null;

    return [...new Set([...outgoing, ...incoming])];
  }

  private nodeName(node: go.Node): string {
    const label = node.data?.labelKey ? this.diagram.findNodeForKey(node.data.labelKey) : null;
    return (label?.data.text as string) || node.data?.category;
  }

  // Busbars have a single whole-shape port (''); other symbols use the free T/R/B/L port closest to the other node.
  private nearestFreePort(node: go.Node, towards: go.Node): string {
    if (node.data.category === 'BB') return '';
    node.ensureBounds();
    towards.ensureBounds();
    const target = towards.actualBounds.center;
    const ports = ['T', 'R', 'B', 'L'].filter(pid => {
      const p = node.findPort(pid);
      return p && p !== node;
    });
    const free = ports.filter(pid => !isPortLinked(node, pid));
    const candidates = free.length ? free : ports;
    let best = candidates[0] ?? '';
    let bestDistance = Infinity;
    for (const pid of candidates) {
      const distance = node.findPort(pid)!.getDocumentPoint(go.Spot.Center).distanceSquaredPoint(target);
      if (distance < bestDistance) { bestDistance = distance; best = pid; }
    }
    return best;
  }

  private initDiagram(): void {
    const $ = go.GraphObject.make;
    this.diagram = $(go.Diagram, 'myDiagramDiv', {
      'undoManager.isEnabled': true,
    });
    this.diagram.toolManager.draggingTool = new SyncLabelDraggingTool();
    this.diagram.toolManager.linkingTool = new PortShowingLinkingTool();
    this.diagram.toolManager.relinkingTool = new BusbarAwareRelinkingTool();
    this._busbarRightClickTool = new BusbarRightClickLinkingTool();
    this.diagram.toolManager.mouseDownTools.insertAt(0, this._busbarRightClickTool);

    this.diagram.model = new go.GraphLinksModel({
      linkFromPortIdProperty: 'fromPort',
      linkToPortIdProperty: 'toPort',
    });

    this.setupDiagramListeners();
    this.setupLinkTemplate();
    this.setupNodeTemplates();
  }

  private setupDiagramListeners(): void {
    this.diagram.addDiagramListener('ViewportBoundsChanged', () => {
      const z = Math.round(this.diagram.scale * 100);
      if (this.zoomPercent !== z) {
        this.zoomPercent = z;
        this.cdr.detectChanges();
      }
    });

    this.diagram.model.addChangedListener((e: go.ChangedEvent) => {
      if (e.change === go.ChangeType.Transaction &&
          (e.propertyName === 'CommittedTransaction' ||
           e.propertyName === 'FinishedUndo' ||
           e.propertyName === 'FinishedRedo')) {
        this.syncUndoRedo();
        this.refreshCimNodeIssues();
        this.markChanged();
      }
      // See the same listener in loadSchematic: redraw undo/redo results in the current theme.
      if (e.change === go.ChangeType.Transaction &&
          (e.propertyName === 'FinishedUndo' || e.propertyName === 'FinishedRedo')) {
        this.applyCanvasTheme(this.drawLight);
      }
    });

    this.diagram.div?.addEventListener('mouseleave', () => cleanupAllPorts(this.diagram));
    this.diagram.div?.addEventListener('contextmenu', (e: MouseEvent) => e.preventDefault());

    const pendingResets: Array<{ node: go.Node; pid: string }> = [];
    // Keyboard Delete bypasses this component's deleteSelection(), so the feeder block is enforced here too.
    const commandHandler = this.diagram.commandHandler;
    commandHandler.deleteSelection = () => {
      let blocked = false;
      this.diagram.selection.each((part: go.Part) => {
        if (!blocked && part instanceof go.Node && this.blockFeederDelete(part)) blocked = true;
      });
      if (!blocked) go.CommandHandler.prototype.deleteSelection.call(commandHandler);
    };

    const isSymbol = (part: go.Part) => part instanceof go.Node && !!SYMBOLS[part.category];
    commandHandler.canCopySelection = () => !this.isLocked && this.diagram.selection.any(isSymbol);
    commandHandler.copySelection = () => {
      this.copiedNodeData = [];
      this.diagram.selection.each((part: go.Part) => {
        if (isSymbol(part)) this.copiedNodeData.push(part.data);
      });
    };
    commandHandler.canPasteSelection = () => !this.isLocked && this.copiedNodeData.length > 0;
    commandHandler.pasteSelection = () => {
      const model = this.diagram.model;
      model.startTransaction('paste');
      for (const data of this.copiedNodeData) {
        if (model.containsNodeData(data)) this.duplicate(data);
      }
      model.commitTransaction('paste');
    };
    commandHandler.canCutSelection = () => false;
    this.diagram.toolManager.draggingTool.isCopyEnabled = false;

    this.diagram.addDiagramListener('SelectionDeleting', (e: go.DiagramEvent) => {
      e.subject.each((part: go.Part) => {
        if (!(part instanceof go.Link)) return;
        const fp = (part.data as any).fromPort as string;
        const tp = (part.data as any).toPort   as string;
        if (part.fromNode && ['T','R','B','L'].includes(fp))
          pendingResets.push({ node: part.fromNode, pid: fp });
        if (part.toNode && ['T','R','B','L'].includes(tp))
          pendingResets.push({ node: part.toNode, pid: tp });
      });
    });
    this.diagram.addDiagramListener('SelectionDeleted', () => {
      pendingResets.forEach(({ node, pid }) => this.resetPort(node, pid));
      pendingResets.length = 0;
    });

    this.diagram.addDiagramListener('ChangedSelection', () => {
      if (this.viewOnly) return;

      let symbolNode: go.Node | null = null;
      let symbolCount = 0;
      let link: go.Link | null = null;
      let linkCount = 0;

      this.diagram.selection.each((part: go.Part) => {
        if (part instanceof go.Node && part.data.category !== 'LABEL') {
          symbolNode = part; symbolCount++;
        }
        if (part instanceof go.Link) { link = part; linkCount++; }
      });

      if (symbolCount === 1) this.onNodeSelected(symbolNode!);
      else if (linkCount === 1 && symbolCount === 0) this.onLinkSelected(link!);
      else {
        this.selectedNode = null;
        this.selectedLink = null;
        this.nodeTopologyLinks = [];
        this.linkTopologyNodes = [];
      }
      this.cdr.detectChanges();
    });

    this.diagram.addDiagramListener('LinkDrawn', (e: go.DiagramEvent) => {
      const link = e.subject as go.Link;

      this.diagram.model.startTransaction('init link');
      this.diagram.model.setDataProperty(link.data, 'color', '#ffffff');

      const dropPt = this.diagram.lastInput.documentPoint;
      if (link.toNode?.data?.category === 'BB') {
        this.diagram.model.setDataProperty(link.data, 'busbarTo', busbarFraction(link.toNode, dropPt));
      }
      if (link.fromNode?.data?.category === 'BB') {
        this.diagram.model.setDataProperty(link.data, 'busbarFrom', this._busbarRightClickTool.pendingFromFrac);
      }

      const markPort = (node: go.Node | null, pid: string) => {
        if (!node || !pid) return;
        const p = node.findPort(pid) as go.Shape;
        if (p instanceof go.Shape) { p.fill = themeColor('#ffffff', this.drawLight); p.opacity = 1; }
      };
      markPort(link.fromNode, (link.data as any).fromPort);
      markPort(link.toNode,   (link.data as any).toPort);
      this.diagram.model.commitTransaction('init link');

      this.selectedLink = link;
      this.linkColor = '#ffffff';
      this.cdr.detectChanges();
    });

    this.diagram.addDiagramListener('LinkRelinked', (e: go.DiagramEvent) => {
      const link = e.subject as go.Link;
      const newPort = e.parameter as go.GraphObject | null;
      const dropPt = this.diagram.lastInput.documentPoint;
      const relinkedToEnd = newPort?.part === link.toNode;

      this.diagram.model.startTransaction('busbar relink');
      if (relinkedToEnd) {
        if (link.toNode?.data?.category === 'BB') {
          this.diagram.model.setDataProperty(link.data, 'busbarTo', busbarFraction(link.toNode, dropPt));
        } else {
          this.diagram.model.setDataProperty(link.data, 'busbarTo', null);
        }
      } else {
        if (link.fromNode?.data?.category === 'BB') {
          this.diagram.model.setDataProperty(link.data, 'busbarFrom', busbarFraction(link.fromNode, dropPt));
        } else {
          this.diagram.model.setDataProperty(link.data, 'busbarFrom', null);
        }
      }
      this.diagram.model.commitTransaction('busbar relink');

      this.resyncPortVisuals();
    });
  }

  private onNodeSelected(node: go.Node): void {
    this.selectedNode = node;
    this.selectedLink = null;
    const lblNode = node.data.labelKey
      ? this.diagram.findNodeForKey(node.data.labelKey) : null;
    this.annotationTag = (lblNode?.data.text as string) || '';
    this.scaleValue = (node.data.nodeScale as number) || 1.0;
    this.selectedColor = (node.data.color as string)
      || SYMBOLS[node.data.category as string]?.defaultColor || '';
    this.selectedState = (node.data.state as string) || 'closed';
    this.nodeLength = (node.data['length'] as number) ||
      (node.data.category === 'LN' ? SYMBOLS['LN'].h : 280);
    this.nodeTopologyLinks = [];
    node.findLinksConnected().each((lnk: go.Link) => {
      const sublabel = lnk.fromNode === node ? 'DESTINATION TERMINAL LINK' : 'SOURCE TERMINAL LINK';
      this.nodeTopologyLinks.push({
        linkData: lnk.data,
        label: 'LINK',
        sublabel,
      });
    });
    this.linkTopologyNodes = [];
  }

  private onLinkSelected(link: go.Link): void {
    this.selectedLink = link;
    this.selectedNode = null;
    this.linkColor = (link.data.color as string) || '#ffffff';
    this.linkTopologyNodes = [];
    [
      { node: link.fromNode, sublabel: 'SOURCE TERMINAL LINK' },
      { node: link.toNode,   sublabel: 'DESTINATION TERMINAL LINK' },
    ].forEach(({ node, sublabel }) => {
      if (!node || node.data.category === 'LABEL') return;
      const nLblKey = node.data.labelKey;
      const nLbl = nLblKey ? this.diagram.findNodeForKey(nLblKey) : null;
      this.linkTopologyNodes.push({
        nodeKey: node.data.key,
        label: (nLbl?.data.text as string) || (node.data.category as string) || 'NODE',
        sublabel,
      });
    });
    this.nodeTopologyLinks = [];
  }

  private portSpot(portId: string | undefined): go.Spot {
    switch (portId) {
      case 'T': return go.Spot.Top;
      case 'R': return go.Spot.Right;
      case 'B': return go.Spot.Bottom;
      case 'L': return go.Spot.Left;
      default: return go.Spot.Default;
    }
  }

  private setupLinkTemplate(): void {
    const $ = go.GraphObject.make;

    const alignTooltip = $(go.Adornment, 'Auto',
      $(go.Shape, 'RoundedRectangle', { fill: '#070e1a', stroke: '#2563eb', strokeWidth: 1 },
        new go.Binding('fill', '', () => this.drawLight ? '#ffffff' : '#070e1a')
      ),
      $(go.TextBlock,
        { margin: 6, stroke: '#ffffff', font: '11px Inter, sans-serif' },
        new go.Binding('stroke', '', () => themeColor('#ffffff', this.drawLight)),
        'Double-click to straighten this connection'
      )
    );

    this.diagram.linkTemplate = $(go.Link,
      {
        routing: go.Routing.Orthogonal,
        selectable: true,
        selectionAdorned: false,
        relinkableFrom: true,
        relinkableTo: true,
        doubleClick: (_e: go.InputEvent, obj: go.GraphObject) => this.alignLink(obj.part as go.Link),
        mouseEnter: (_e: go.InputEvent, obj: go.GraphObject) => {
          const link = obj.part as go.Link;
          const arrow = link.findObject('DIRECTION_ARROW');
          if (arrow) arrow.opacity = 1;
          link.toolTip = (this.isLocked || this.isLinkAligned(link)) ? null : alignTooltip;
        },
        mouseLeave: (_e: go.InputEvent, obj: go.GraphObject) => {
          const link = obj.part as go.Link;
          const arrow = link.findObject('DIRECTION_ARROW');
          if (arrow) arrow.opacity = link.isSelected ? 1 : 0;
        },
        selectionChanged: (link: go.Part) => {
          const lnk = link as go.Link;
          const arrow = lnk.findObject('DIRECTION_ARROW');
          if (arrow) arrow.opacity = lnk.isSelected ? 1 : 0;
        },
      },
      new go.Binding('toSpot', '', (d: any, obj: go.GraphObject) => {
        if (d.busbarTo == null) return this.portSpot(d.toPort);
        const link = obj as go.Link;
        return link.toNode && link.fromNode
          ? this.busbarAttachSpot(link.toNode, link.fromNode, d.busbarTo)
          : new go.Spot(d.busbarTo, 0.5);
      }),
      new go.Binding('fromSpot', '', (d: any, obj: go.GraphObject) => {
        if (d.busbarFrom == null) return this.portSpot(d.fromPort);
        const link = obj as go.Link;
        return link.fromNode && link.toNode
          ? this.busbarAttachSpot(link.fromNode, link.toNode, d.busbarFrom)
          : new go.Spot(d.busbarFrom, 0.5);
      }),
      $(go.Shape, { isPanelMain: true },
        new go.Binding('stroke', 'color', (c: string) => themeColor(c || '#ffffff', this.drawLight)),
        new go.Binding('strokeWidth', 'isSelected', (s: boolean) => s ? 4 : 1.5).ofObject()
      ),
      $(go.Shape, {
        name: 'DIRECTION_ARROW',
        toArrow: 'Standard',
        stroke: null,
        opacity: 0,
      },
        new go.Binding('fill', 'color', (c: string) => themeColor(c || '#ffffff', this.drawLight)),
        new go.Binding('scale', 'isSelected', (s: boolean) => s ? 1.8 : 1).ofObject()
      )
    );
  }

  private alignLink(link: go.Link): void {
    if (this.isLocked) return;
    const target = this.alignTarget(link);
    if (target) this.moveNodeAndLabel(target.node, target.newLoc);
  }

  private isLinkAligned(link: go.Link): boolean {
    const target = this.alignTarget(link);
    if (!target) return true;
    const loc = target.node.location;
    const EPSILON = 0.5;
    return Math.abs(loc.x - target.newLoc.x) < EPSILON && Math.abs(loc.y - target.newLoc.y) < EPSILON;
  }

  private effectivePortDirection(node: go.Node, portId: string | undefined): string | undefined {
    const NOMINAL_ANGLE: Record<string, number> = { R: 0, B: 90, L: 180, T: 270 };
    if (!portId || !(portId in NOMINAL_ANGLE)) return undefined;
    const DIRECTION_FOR_ANGLE: Record<number, string> = { 0: 'R', 90: 'B', 180: 'L', 270: 'T' };
    const nodeAngle = (node.data?.angle as number) || 0;
    const actual = ((NOMINAL_ANGLE[portId] + nodeAngle) % 360 + 360) % 360;
    return DIRECTION_FOR_ANGLE[actual];
  }

  private alignTarget(link: go.Link): { node: go.Node; newLoc: go.Point } | null {
    const fromNode = link.fromNode;
    const toNode = link.toNode;
    if (!fromNode || !toNode) return null;

    const fromIsBB = fromNode.data?.category === 'BB';
    const toIsBB = toNode.data?.category === 'BB';
    if (fromIsBB && toIsBB) return null;

    if (fromIsBB || toIsBB) {
      const busbar = fromIsBB ? fromNode : toNode;
      const other = fromIsBB ? toNode : fromNode;

      const n = link.pointsCount;
      if (n < 2) return null;
      const busbarPt = fromIsBB ? link.getPoint(0) : link.getPoint(n - 1);
      const otherPt = fromIsBB ? link.getPoint(n - 1) : link.getPoint(0);

      const otherPort = this.effectivePortDirection(
        other,
        fromIsBB ? (link.data as any).toPort as string | undefined : (link.data as any).fromPort as string | undefined
      );
      const isVertical = (((busbar.data.angle as number) || 0) % 180) === 90;
      const requiredPort = isVertical
        ? (busbarPt.x < otherPt.x ? 'L' : 'R')
        : (busbarPt.y < otherPt.y ? 'T' : 'B');
      if (!otherPort || otherPort !== requiredPort) return null;

      const otherLoc = other.location;
      const newLoc = isVertical
        ? new go.Point(otherLoc.x, otherLoc.y + (busbarPt.y - otherPt.y))
        : new go.Point(otherLoc.x + (busbarPt.x - otherPt.x), otherLoc.y);
      return { node: other, newLoc };
    }

    const fromPort = this.effectivePortDirection(fromNode, (link.data as any).fromPort as string | undefined);
    const toPort = this.effectivePortDirection(toNode, (link.data as any).toPort as string | undefined);
    const OPPOSITE_PORT: Record<string, string> = { T: 'B', B: 'T', L: 'R', R: 'L' };
    if (!fromPort || !toPort || OPPOSITE_PORT[fromPort] !== toPort) return null;

    const n = link.pointsCount;
    if (n < 2) return null;
    const fromPt = link.getPoint(0);
    const toPt = link.getPoint(n - 1);
    const toLoc = toNode.location;

    const MIN_ALIGN_GAP = 60;
    const isVertical = fromPort === 'T' || fromPort === 'B';
    const requiredSign = (fromPort === 'B' || fromPort === 'R') ? 1 : -1;
    const currentGap = isVertical ? (toPt.y - fromPt.y) : (toPt.x - fromPt.x);
    const signedMin = requiredSign * MIN_ALIGN_GAP;
    const finalGap = requiredSign > 0 ? Math.max(currentGap, signedMin) : Math.min(currentGap, signedMin);
    const gapDelta = finalGap - currentGap;

    const newToLoc = isVertical
      ? new go.Point(toLoc.x + (fromPt.x - toPt.x), toLoc.y + gapDelta)
      : new go.Point(toLoc.x + gapDelta, toLoc.y + (fromPt.y - toPt.y));
    return { node: toNode, newLoc: newToLoc };
  }

  private busbarAttachSpot(busbar: go.Node, other: go.Node, frac: number): go.Spot {
    const bb = SYMBOLS['BB'];
    const surfaceOffset = bb.h / 2 - (bb.strokeWidth ?? 0) / 2;
    const topPt = busbar.getDocumentPoint(new go.Spot(frac, 0));
    const bottomPt = busbar.getDocumentPoint(new go.Spot(frac, 1));
    const otherLoc = other.location;
    return topPt.distanceSquaredPoint(otherLoc) <= bottomPt.distanceSquaredPoint(otherLoc)
      ? new go.Spot(frac, 0, 0, surfaceOffset)
      : new go.Spot(frac, 1, 0, -surfaceOffset);
  }

  private moveNodeAndLabel(node: go.Node, newLoc: go.Point): void {
    const loc = node.location;
    const offset = new go.Point(newLoc.x - loc.x, newLoc.y - loc.y);
    if (offset.x === 0 && offset.y === 0) return;

    this.diagram.model.startTransaction('align link');
    this.diagram.model.setDataProperty(node.data, 'loc', go.Point.stringify(newLoc));
    const lblKey = node.data?.labelKey as string | undefined;
    const lblNode = lblKey ? this.diagram.findNodeForKey(lblKey) : null;
    if (lblNode) {
      const lblLoc = lblNode.location;
      this.diagram.model.setDataProperty(
        lblNode.data, 'loc',
        go.Point.stringify(new go.Point(lblLoc.x + offset.x, lblLoc.y + offset.y))
      );
    }
    this.diagram.model.commitTransaction('align link');
  }

  private setupNodeTemplates(): void {
    const $ = go.GraphObject.make;

    this.diagram.nodeTemplateMap.add('LABEL',
      $(go.Node, 'Auto',
        {
          locationSpot: go.Spot.Center,
          movable: true,
          selectable: true,
          cursor: 'move',
          selectionAdorned: false,
          selectionChanged: (node: go.Part) => {
            const masterNode = (node as go.Node).data.master
              ? node.diagram?.findNodeForKey((node as go.Node).data.master)
              : null;
            if (masterNode) masterNode.isSelected = node.isSelected;
          },
          mouseEnter: (_e: go.InputEvent, node: go.GraphObject) => {
            const n = node as go.Node;
            if (!n.diagram || !n.data?.hiddenUntilHover) return;
            withoutUndo(n.diagram, () => { n.opacity = 1; });
          },
          mouseLeave: (_e: go.InputEvent, node: go.GraphObject) => {
            const n = node as go.Node;
            if (!n.diagram || !n.data?.hiddenUntilHover) return;
            withoutUndo(n.diagram, () => { n.opacity = 0; });
          },
        },
        new go.Binding('location', 'loc', go.Point.parse).makeTwoWay(go.Point.stringify),
        new go.Binding('angle', 'labelAngle'),
        new go.Binding('opacity', 'hiddenUntilHover', (hidden: boolean) => hidden ? 0 : 1),
        $(go.Shape, 'RoundedRectangle',
          {
            parameter1: 6,
            strokeWidth: 1.5,
            strokeDashArray: [5, 3],
          },
          new go.Binding('fill', 'isSelected', (s: boolean) => s ? (this.drawLight ? '#eff6ff' : '#070e1a') : 'transparent').ofObject(),
          new go.Binding('stroke', 'isSelected', (s: boolean) => s ? '#2563eb' : null).ofObject(),
        ),
        $(go.TextBlock,
          {
            stroke: '#ffffff',
            font: 'bold 10px Inter, sans-serif',
            margin: new go.Margin(6, 14),
          },
          new go.Binding('text', 'text'),
          new go.Binding('stroke', '', () => themeColor('#ffffff', this.drawLight))
        )
      )
    );

    const isLight = () => this.drawLight;
    for (const [cat, sym] of Object.entries(SYMBOLS)) {
      this.diagram.nodeTemplateMap.add(cat,
        cat === 'BB' ? busbarNode(sym, () => this.isLocked, isLight) :
        cat === 'LN' ? acLineNode(sym, isLight) :
        symNode(sym, isLight)
      );
    }
  }
}
